/**
 * TRANSACTIONS HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getTransactionDetails()    - GET /transactions/:id - Fetch specific transaction details
 * • getTransactions()          - GET /transactions - Fetch paginated transactions with filters
 * • cancelTransaction()        - POST /transactions/:id/cancel - Cancel a pending bank transaction
 * • downloadTransactionInvoice() - GET /transactions/:id/invoice/export - Download transaction invoice as PDF
 * • downloadTransactionReceipt() - GET /transactions/:id/receipt/export - Download transaction receipt as PDF
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useTransactionDetails(transactionId, enabled?)  - Query: Get transaction by ID
 * • useFetchTransactionDetails()                     - Mutation: Manually fetch transaction details
 * • useTransactions(filters?, enabled?)              - Query: Get paginated transactions with filters
 * • useCancelTransaction()                           - Mutation: Cancel a pending bank transaction
 * • useDownloadTransactionInvoice()                  - Mutation: Download transaction invoice as PDF
 * • useDownloadTransactionReceipt()                   - Mutation: Download transaction receipt as PDF
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • Comprehensive filtering (wallet_id, type, status, provider, dates, search, team_member_ids)
 * • Invoice and receipt download support for eligible transactions
 * • Standardized response handling with data array and pagination object
 * • 5-minute stale time with disabled auto-refetch
 * • Automatic query invalidation on mutations
 * • Support for transaction types: 'top-up', 'withdrawal'
 * • Support for transaction statuses: 'pending', 'failed', 'declined', 'completed', 'reserved', 'cancelled', 'refunded'
 * • Support for transaction providers: 'bank', 'card'
 * • Support for filtering by team member IDs (for employers)
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DEFAULT_PAGE_SIZE } from '@workspace/ui/lib';
import type { ApiResponse } from '@workspace/ui/types';
import { toast } from 'sonner';
import fetchAPI from '@/lib/fetch-api';

// ==================== INTERFACES ====================

export interface TransactionMetadata {
  gig_platform_id?: number | string;
}

interface TransactionFees {
  percent: number;
  value: number;
}

interface TransactionData {
  id: number;
  type: string;
  status: string;
  amount: number;
  currency: string;
  created_at: string;
  description: string;
  reference: string;
  fees: TransactionFees;
}

interface Currency {
  id: number;
  name: string;
  code: string;
  flag_code: string;
  symbol: string;
  billing_currency: boolean;
}

interface TransactionItem {
  id: number;
  type: string;
  status: string;
  amount: number;
  currency: string;
  balance: number;
  description: string;
  provider: string;
  fees: TransactionFees;
  transfer_type?: string;
  currency_id: number;
  wallet_id: number;
  bank_account: string | null;
  user_name: string;
  user_id: number;
  user_email: string;
  reference: string;
  date_of_request: string;
  date_of_payment: string | null;
  date_of_declined: string | null;
  date_of_rejection: string | null;
  converted_amount: string;
  fee: string;
  tax: number;
  tax_converted: number;
  converted_fee: string;
  total_amount: string;
  total_converted_amount: string;
  from_currency: Currency;
  to_currency: Currency;
  recipient?: {
    id: number;
    name: string;
    email: string;
  };
  metadata?: TransactionMetadata | null;
}

interface TransactionListData {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
  items: TransactionItem[];
}

interface NormalizedTransactionPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
}

interface InfiniteTransactionsPage<TData = TransactionItem> {
  data: TData[];
  pagination: NormalizedTransactionPagination;
}

interface CancelTransactionData {
  id: number;
  status: string;
  is_new: boolean;
  amount: number;
  updated_at: string;
}

export interface TransactionFilters {
  wallet_id?: number;
  type?: 'inbound' | 'outbound' | 'internal';
  types?: ('inbound' | 'outbound' | 'internal')[]; // Support multiple types
  transfer_type?: string; // Single transfer type filter (e.g., 'expense', 'payroll')
  transfer_types?: string[]; // Support multiple transfer types
  status?: 'pending' | 'failed' | 'declined' | 'completed' | 'reserved' | 'cancelled' | 'refunded';
  statuses?: string[]; // Support multiple statuses
  provider?: 'bank' | 'card';
  currency?: string; // Currency code, e.g. 'EUR'
  date_from?: string;
  date_to?: string;
  search?: string;
  team_member_ids?: number[]; // Array of team member user IDs
  gig_platform_ids?: number[];
  page?: number;
  per_page?: number;
}

/**
 * Keyed off the whole filter object so a newly added filter can never be left out
 * of the cache key and silently serve stale results.
 */
const buildTransactionsQueryKey = (filters: TransactionFilters) => ['transactions', filters];

// ==================== API FUNCTIONS ====================

function normalizeTransactionPagination(
  pagination: ApiResponse['pagination'] | Record<string, unknown> | undefined,
  itemsCount: number
): NormalizedTransactionPagination {
  const raw = (pagination ?? {}) as Record<string, unknown>;
  const currentPage = Number(raw.current_page ?? raw.currentPage ?? 1);
  const perPage = Number(raw.per_page ?? raw.perPage ?? DEFAULT_PAGE_SIZE);
  const total = Number(raw.total ?? 0);
  let lastPage = Number(raw.last_page ?? raw.lastPage ?? 0);

  if (!Number.isFinite(lastPage) || lastPage <= 0) {
    lastPage =
      perPage > 0 && Number.isFinite(total) && total > 0 ? Math.ceil(total / perPage) : currentPage;
  }

  const hasMoreFlag = raw.has_more ?? raw.hasMore;
  const hasMore =
    typeof hasMoreFlag === 'boolean'
      ? hasMoreFlag
      : currentPage < lastPage ||
        (itemsCount >= perPage && Number.isFinite(total) && currentPage * perPage < total);

  return {
    current_page: currentPage,
    per_page: perPage,
    total,
    last_page: lastPage,
    has_more: hasMore,
  };
}

function getNextTransactionsPageParam<TData>(
  lastPage: InfiniteTransactionsPage<TData>,
  allPages: InfiniteTransactionsPage<TData>[],
  lastPageParam: number
): number | undefined {
  const { pagination, data } = lastPage;
  const nextPage = lastPageParam + 1;
  const fetchedCount = allPages.reduce((acc, page) => acc + page.data.length, 0);

  if (pagination.has_more) {
    return nextPage;
  }

  if (nextPage <= pagination.last_page) {
    return nextPage;
  }

  if (
    data.length >= pagination.per_page &&
    Number.isFinite(pagination.total) &&
    fetchedCount < pagination.total
  ) {
    return nextPage;
  }

  return undefined;
}

/**
 * Retrieves detailed information about a specific transaction for the authenticated user
 * @param {number} transactionId - The transaction identifier
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<TransactionData>} The transaction details
 */
const getTransactionDetails = async (transactionId: number): Promise<TransactionData> => {
  if (!transactionId) {
    throw new Error('Transaction ID is required');
  }

  const response = await fetchAPI<TransactionData>(`/transactions/${transactionId}`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch transaction details');
  }

  return response.data!;
};

/**
 * Fetches a paginated list of transactions with optional filters
 * @param {TransactionFilters} filters - Optional filters for the transaction list
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<TransactionListData>} The paginated transaction list
 */
const getTransactions = async (filters: TransactionFilters = {}): Promise<TransactionListData> => {
  // Build query parameters
  const searchParams = new URLSearchParams();

  if (filters.wallet_id) searchParams.append('wallet_id', filters.wallet_id.toString());
  if (filters.type) searchParams.append('type', filters.type);
  if (filters.types && filters.types.length > 0) {
    filters.types.forEach((type) => searchParams.append('types[]', type));
  }
  if (filters.transfer_type) searchParams.append('transfer_type', filters.transfer_type);
  if (filters.transfer_types && filters.transfer_types.length > 0) {
    filters.transfer_types.forEach((transferType) =>
      searchParams.append('transfer_type[]', transferType)
    );
  }
  if (filters.status) searchParams.append('status', filters.status);
  if (filters.statuses && filters.statuses.length > 0) {
    filters.statuses.forEach((status) => searchParams.append('status[]', status));
  }
  if (filters.provider) searchParams.append('provider', filters.provider);
  if (filters.currency) searchParams.append('currency', filters.currency);

  if (filters.date_from) searchParams.append('date_from', filters.date_from);
  if (filters.date_to) searchParams.append('date_to', filters.date_to);
  if (filters.search) searchParams.append('search', filters.search);
  if (filters.team_member_ids && filters.team_member_ids.length > 0) {
    filters.team_member_ids.forEach((id) =>
      searchParams.append('team_member_ids[]', id.toString())
    );
  }
  if (filters.gig_platform_ids && filters.gig_platform_ids.length > 0) {
    filters.gig_platform_ids.forEach((id) =>
      searchParams.append('gig_platform_ids[]', id.toString())
    );
  }
  if (filters.page) searchParams.append('page', filters.page.toString());
  if (filters.per_page) searchParams.append('per_page', filters.per_page.toString());

  const queryString = searchParams.toString();
  const url = `/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await fetchAPI<TransactionItem[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch transactions');
  }

  // Handle response format with data array and pagination object
  if (!response.data || !response.pagination) {
    throw new Error('Invalid response format: missing data or pagination');
  }

  const normalizedPagination = normalizeTransactionPagination(
    response.pagination,
    response.data.length
  );

  return {
    current_page: normalizedPagination.current_page,
    per_page: normalizedPagination.per_page,
    total: normalizedPagination.total,
    last_page: normalizedPagination.last_page,
    has_more: normalizedPagination.has_more,
    items: response.data!,
  };
};

/**
 * Cancels a pending bank transaction
 * @param {number} transactionId - The transaction identifier to cancel
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<CancelTransactionData>} The cancelled transaction data
 */
const cancelTransaction = async (transactionId: number): Promise<CancelTransactionData> => {
  if (!transactionId) {
    throw new Error('Transaction ID is required');
  }

  const response = await fetchAPI<CancelTransactionData>(`/transactions/${transactionId}/cancel`, {
    method: 'PATCH',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to cancel transaction');
  }

  return response.data!;
};

/**
 * Downloads the invoice PDF for a specific transaction
 * @param {number} transactionId - The transaction identifier
 * @throws {Error} If the transaction ID is invalid or the API request fails
 * @returns {Promise<Blob>} A PDF blob containing the transaction invoice
 */
const downloadTransactionInvoice = async (transactionId: number): Promise<Blob> => {
  if (!transactionId || transactionId <= 0) {
    throw new Error('Invalid transaction ID provided');
  }

  const response = await fetchAPI<Blob>(`/transactions/${transactionId}/invoice/export`, {
    method: 'GET',
    responseType: 'blob',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to download transaction invoice');
  }

  return response.data!;
};

/**
 * Downloads the receipt PDF for a specific transaction
 * @param {number} transactionId - The transaction identifier
 * @throws {Error} If the transaction ID is invalid, no authentication token is found, or the API request fails
 * @returns {Promise<Blob>} A PDF blob containing the transaction receipt
 */
const downloadTransactionReceipt = async (transactionId: number): Promise<Blob> => {
  if (!transactionId || transactionId <= 0) {
    throw new Error('Invalid transaction ID provided');
  }

  const response = await fetchAPI<Blob>(`/transactions/${transactionId}/receipt/export`, {
    method: 'GET',
    responseType: 'blob',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to download transaction receipt');
  }

  return response.data!;
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to fetch transaction details for a specific transaction ID
 * @param {number} transactionId - The transaction ID to fetch details for
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for transaction details
 */
export function useTransactionDetails(
  transactionId: number,
  enabled: boolean = true
): UseQueryResult<TransactionData, Error> {
  return useQuery({
    queryKey: ['transaction-details', transactionId],
    queryFn: () => getTransactionDetails(transactionId),
    enabled: enabled && !!transactionId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to fetch transaction details (as a mutation for manual triggering)
 * @returns {UseMutationResult} Mutation result for fetching transaction details
 */
export function useFetchTransactionDetails(): UseMutationResult<TransactionData, Error, number> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: getTransactionDetails,
    onSuccess: (data, transactionId) => {
      // Update the query cache with the new data
      queryClient.setQueryData(['transaction-details', transactionId], data);

      // Invalidate transaction-related queries
      queryClient.invalidateQueries({ queryKey: ['transaction-details'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to fetch a paginated list of transactions with optional filters
 * @param {TransactionFilters} filters - Optional filters for the transaction list
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for transactions list
 */
export function useInfiniteTransactions<TData = TransactionItem>(
  filters: Omit<TransactionFilters, 'page'> = {},
  enabled: boolean = true,
  mapItem: (item: TransactionItem) => TData = (item) => item as TData
): UseInfiniteQueryResult<InfiniteTransactionsPage<TData>, Error> {
  const perPage = filters.per_page ?? DEFAULT_PAGE_SIZE;

  const queryKey = buildTransactionsQueryKey({ ...filters, per_page: perPage });

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const response = await getTransactions({
        ...filters,
        page: pageParam,
        per_page: perPage,
      });

      const pagination = normalizeTransactionPagination(
        {
          current_page: response.current_page,
          per_page: response.per_page,
          total: response.total,
          last_page: response.last_page,
          has_more: response.has_more,
        },
        response.items.length
      );

      return {
        data: response.items.map(mapItem),
        pagination,
      };
    },
    getNextPageParam: getNextTransactionsPageParam,
    initialPageParam: 1,
    enabled,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

export function useTransactions(
  filters: TransactionFilters = {},
  enabled: boolean = true
): UseQueryResult<TransactionListData, Error> {
  const queryKey = buildTransactionsQueryKey(filters);

  return useQuery({
    queryKey,
    queryFn: () => getTransactions(filters),
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to cancel a pending bank transaction
 * @returns {UseMutationResult} Mutation result for cancelling a transaction
 */
export function useCancelTransaction(): UseMutationResult<CancelTransactionData, Error, number> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelTransaction,
    onSuccess: (data, transactionId) => {
      // Update the specific transaction in the cache
      queryClient.setQueryData(
        ['transaction-details', transactionId],
        (oldData: TransactionData | undefined) => {
          if (oldData) {
            return { ...oldData, status: data.status };
          }
          return oldData;
        }
      );

      // Invalidate transaction-related queries to refresh the lists
      queryClient.invalidateQueries({ queryKey: ['transaction-details'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to download a transaction invoice as a PDF
 * @returns {UseMutationResult} Mutation result for downloading a transaction invoice
 */
export function useDownloadTransactionInvoice(): UseMutationResult<Blob, Error, number> {
  return useMutation({
    mutationFn: downloadTransactionInvoice,
    onSuccess: (blob, transactionId) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transaction-invoice-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to download transaction invoice';
      toast.error(errorMessage);
    },
  });
}

/**
 * Hook to download a transaction receipt as a PDF
 * @returns {UseMutationResult} Mutation result for downloading a transaction receipt
 */
export function useDownloadTransactionReceipt(): UseMutationResult<Blob, Error, number> {
  return useMutation({
    mutationFn: downloadTransactionReceipt,
    onSuccess: (blob, transactionId) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transaction-receipt-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to download transaction receipt';
      toast.error(errorMessage);
    },
  });
}
